import './Autosuggest.scss';

import * as React from 'react';

import cx from 'classnames';

import { Announcement } from '~shared/components/Announcement';
import { normalizeKey } from '~shared/utils/normalize-keycode';

import { Spinner } from '~shared/components/Spinner/Spinner';

import { FocusIndicator } from '../FocusIndicator/FocusIndicator';
import {
  HAS_SUGGESTIONS_CHANGED,
  INPUT_ARROW_DOWN,
  INPUT_ARROW_UP,
  INPUT_BLUR,
  INPUT_CHANGE,
  INPUT_ENTER,
  INPUT_ESCAPE,
  INPUT_FOCUS,
  SUGGESTION_MOUSE_CLICK,
  SUGGESTION_MOUSE_ENTER,
} from './actions';
import {
  createAccessibilityProps,
  getItemId,
} from './createAccessibilityProps';
import {
  AutosuggestTranslations,
  AutosuggestValue,
  Suggestion,
  Suggestions,
} from './types';
import { useAutosuggestReducer } from './useAutosuggestReducer';
import { useScrollIntoView } from './useScrollIntoView';

interface SuggestionItemProps {
  suggestion: Suggestion;
  highlighted: boolean;
  selected: boolean;
  onClick: () => void;
  onHover: () => void;
}

function SuggestionItem({
  suggestion,
  highlighted,
  selected,
  onHover,
  ...restProps
}: SuggestionItemProps) {
  const label = suggestion.label ?? suggestion.text;

  const itemClass = cx('autosuggest__item', 'px-6 py-4', [
    (highlighted || selected) && 'is-highlighted',
    (highlighted || selected) && 'bg-grey-50',
    selected && 'font-600',
  ]);

  return (
    <li
      className="cursor-pointer"
      onMouseDown={(event) => {
        // Without this `onClick` will not fire due to the input blur event
        event.preventDefault();
      }}
      onMouseMove={onHover}
      onTouchStart={onHover}
      {...restProps}
    >
      {/*
          Render styles as a separate element, otherwise VoiceOver can't
          keep up with the updated ARIA attributes. Don't ask me why, but
          it seems that too many changes to the element causes either React
          or the browser or VoiceOver to see the list item as a new element.
      */}
      <div className={itemClass}>
        <span className="block text-sm font-700 leading-7 text-legacy-blue-400">
          {label}
        </span>
        {suggestion.description ? (
          <span className="block text-sm leading-7 text-grey-400">
            {suggestion.description}
          </span>
        ) : null}
      </div>
    </li>
  );
}

function valueFromSuggestion<Value>(
  suggestion: Suggestion<Value>
): AutosuggestValue<Value> {
  return 'value' in suggestion
    ? {
        text: suggestion.text,
        value: suggestion.value,
      }
    : { text: suggestion.text };
}

const noop = () => {
  /**/
};

const fallbackValue = { text: '' };
const fallbackSuggestions: Suggestion[] = [];

export interface AutosuggestProps<Value> {
  id: string;
  name?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  maxLength?: number;
  'aria-labelledby': React.AriaAttributes['aria-labelledby'];
  value: AutosuggestValue<Value>;
  suggestions:
    | Suggestions<Value>
    | { message: React.ReactNode }
    | ((
        value: AutosuggestValue<Value>
      ) => Suggestions<Value> | { message: React.ReactNode });
  onChange: (value: AutosuggestValue<Value>) => void;
  automaticSelection?: boolean;
  hideSuggestionsOnSelection?: boolean;
  onBlur?: () => void;
  onFocus?: () => void;
  placeholder?: string;
  type?: 'text' | 'search';
  translations?: AutosuggestTranslations;
  showSpinner?: boolean;
  pinnedItem?: React.ReactNode;
  noResultsNode?: React.ReactNode;
  containerClass?: string;
}

export const Autosuggest = React.forwardRef(function Autosuggest<Value>(
  {
    id,
    value = fallbackValue,
    suggestions: suggestionsProp = fallbackSuggestions,
    onChange = noop,
    automaticSelection = false,
    hideSuggestionsOnSelection = true,
    onFocus = noop,
    onBlur = noop,
    placeholder,
    type = 'text',
    translations,
    showSpinner,
    pinnedItem,
    noResultsNode = null,
    containerClass,
    ...restProps
  }: AutosuggestProps<Value>,
  forwardedRef: React.Ref<HTMLInputElement>
) {
  const suggestionsPropValue =
    typeof suggestionsProp === 'function'
      ? suggestionsProp(value)
      : suggestionsProp;

  const suggestions = Array.isArray(suggestionsPropValue)
    ? suggestionsPropValue
    : [];
  const message =
    'message' in suggestionsPropValue
      ? suggestionsPropValue.message
      : undefined;
  const hasItems = suggestions.length > 0 || Boolean(message);

  const defaultRef = React.useRef<HTMLInputElement | null>(null);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const menuRef = React.useRef<HTMLUListElement>(null);
  const justPressedArrowRef = React.useRef(false);
  const inputRef = forwardedRef || defaultRef;

  const suggestionCount = suggestions.length;
  const hasSuggestions = suggestionCount > 0;

  const fireChange = React.useCallback(
    (suggestion: Suggestion<Value>) =>
      onChange(valueFromSuggestion(suggestion)),
    [onChange]
  );

  const [
    {
      showSuggestionsIfAvailable,
      inputChangedSinceFocus,
      previewValue,
      highlightedIndex,
      isFocused,
    },
    dispatch,
  ] = useAutosuggestReducer({
    automaticSelection,
    hasItems,
    hasSuggestions,
    hideSuggestionsOnSelection,
    suggestionCount,
    suggestions,
    value,
  });

  const isOpen =
    (showSuggestionsIfAvailable && hasItems) ||
    showSpinner ||
    (!!noResultsNode && isFocused && !hasItems);

  const highlightedItem =
    typeof highlightedIndex === 'number'
      ? document.getElementById(getItemId(id, highlightedIndex))
      : null;

  useScrollIntoView(highlightedItem, menuRef.current);

  React.useEffect(() => {
    dispatch({
      type: HAS_SUGGESTIONS_CHANGED,
    });
  }, [hasSuggestions, dispatch]);

  const inputProps = {
    value: previewValue ? previewValue.text : value.text,
    type: type === 'search' ? type : 'text',
    placeholder,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;

      dispatch({ type: INPUT_CHANGE });
      fireChange({ text: inputValue });
    },
    onFocus: (e: React.ChangeEvent<HTMLInputElement>) => {
      e.target.select();
      dispatch({ type: INPUT_FOCUS });
      onFocus();
    },
    onBlur: () => {
      if (justPressedArrowRef.current === true) return;

      if (previewValue) {
        fireChange(previewValue);
      } else if (
        isOpen &&
        automaticSelection &&
        inputChangedSinceFocus &&
        value.text.length > 0 &&
        suggestionCount > 0
      ) {
        fireChange(suggestions[0]);
      }

      dispatch({ type: INPUT_BLUR });
      onBlur();
    },
    onKeyDown: (event: React.KeyboardEvent) => {
      const targetKey = normalizeKey(event);

      /**
       * Fix bug in Chrome + VoiceOver where the input is blurred when
       * pressing arrow up/down.
       */
      if (/^Arrow(Up|Down$)/.test(targetKey)) {
        justPressedArrowRef.current = true;
        setTimeout(() => {
          justPressedArrowRef.current = false;
        }, 150);
      }

      switch (targetKey) {
        case 'ArrowDown': {
          event.preventDefault();
          dispatch({ type: INPUT_ARROW_DOWN });
          return;
        }

        case 'ArrowUp': {
          event.preventDefault();
          dispatch({ type: INPUT_ARROW_UP });
          return;
        }

        case 'Escape': {
          event.preventDefault();
          if (previewValue === null && value.text) {
            fireChange({ text: '' });
          }

          dispatch({ type: INPUT_ESCAPE });
          return;
        }

        case 'Enter': {
          if (typeof highlightedIndex === 'number') {
            event.preventDefault();
            fireChange(suggestions[highlightedIndex]);
          }

          dispatch({ type: INPUT_ENTER });
          // @ts-expect-error -- `current` *does* exist on this ref
          inputRef.current.blur();
          return;
        }

        default: {
          return;
        }
      }
    },
  };

  const a11y = createAccessibilityProps({
    id,
    isOpen,
    highlightedIndex,
  });

  const announcements = [];
  const hasAutomaticSelection =
    automaticSelection && previewValue === null && highlightedIndex === 0;

  /**
   * Announce when the field is focused and no selections have been
   * manually highlighted.
   */
  if (
    isFocused &&
    isOpen &&
    (highlightedIndex === null || hasAutomaticSelection)
  ) {
    if (message) {
      announcements.push(message);
    }
    if (hasSuggestions) {
      announcements.push(
        translations?.suggestionsAvailableAnnouncement(suggestionCount)
      );
      if (hasAutomaticSelection) {
        announcements.push(
          translations?.suggestionAutoSelectedAnnouncement(suggestions[0].text)
        );
      }
      announcements.push(translations?.suggestionInstructions);
    } else {
      announcements.push(translations?.noSuggestionsAvailableAnnouncement);
    }
  }

  const inputClass = cx(
    'autosuggest__input',
    'relative flex w-full px-5 py-4 text-base text-grey-700 border-0 bg-white shadow-legacy-border outline-none'
  );
  const menuClass = cx(
    'autosuggest__menu',
    'absolute z-50 flex flex-col w-full min-h-0 max-h-60 md:max-h-80 bg-white overflow-hidden shadow-lg',
    [isOpen ? 'is-open' : 'hidden']
  );

  return (
    <div className={containerClass}>
      <div className="relative" ref={rootRef}>
        <div data-field={a11y.labelProps.id} data-value={value.text}>
          <div className="group w-full">
            <input
              {...restProps}
              {...a11y.inputProps}
              {...inputProps}
              className={inputClass}
              ref={inputRef}
            />
            <FocusIndicator notOffset />
            <div className={menuClass}>
              {showSpinner ? (
                <div
                  className="flex py-4"
                  style={{
                    position: 'relative',
                    height: '3.5rem',
                    padding: '12px',
                    margin: '0 auto',
                  }}
                >
                  <Spinner />
                </div>
              ) : (
                <>
                  <ul
                    className="px-0 py-1 mt-1 mb-0 list-none overflow-y-auto"
                    ref={menuRef}
                    {...a11y.menuProps}
                  >
                    {isOpen && message ? <li className="">{message}</li> : null}
                    {isOpen && hasSuggestions
                      ? suggestions.map((suggestion, index) => {
                          const { text } = suggestion;

                          return (
                            <SuggestionItem
                              key={index + text}
                              suggestion={suggestion}
                              highlighted={highlightedIndex === index}
                              selected={value === suggestion}
                              onClick={() => {
                                fireChange(suggestion);
                                dispatch({ type: SUGGESTION_MOUSE_CLICK });
                                // @ts-expect-error -- `current` *does* exist on this ref
                                inputRef.current.blur();
                              }}
                              onHover={() => {
                                dispatch({
                                  type: SUGGESTION_MOUSE_ENTER,
                                  value: index,
                                });
                              }}
                              {...a11y.getItemProps({
                                index,
                                label: suggestion.label ?? suggestion.text,
                                description: suggestion.description,
                              })}
                            />
                          );
                        })
                      : noResultsNode}
                  </ul>
                  {pinnedItem ? pinnedItem : null}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="sr-only" {...a11y.assistiveDescriptionProps}>
        {translations?.assistiveDescription}
      </div>
      <Announcement>{announcements.join('. ')}</Announcement>
    </div>
  );
}) as <Value>(
  props: AutosuggestProps<Value> & React.RefAttributes<HTMLInputElement>
) => React.ReactElement;
